import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  LifeBuoy,
  Lightbulb,
  Search,
  Send,
  Sparkles,
} from "lucide-react";
import { fetchAiSearch, normalizeSearchRow } from "@/lib/ai-search-api";
import { askAiChatbot } from "@/lib/ai-segments-api";
import { toast } from "sonner";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { getAuthSession } from "@/lib/auth-session";
import { API_BASE } from "@/lib/utils";
import {
  HELP_CATEGORIES,
  HELP_MODULE_OPTIONS,
  HELP_TABS,
  searchHelpArticles,
} from "@/content/help-knowledge-base";

function HelpTabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-[var(--primary)] text-[var(--text-primary)]"
          : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      }`}
    >
      {children}
    </button>
  );
}

function KnowledgeBasePanel({ aiEnabled = false }) {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [aiHits, setAiHits] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const results = useMemo(() => searchHelpArticles(query), [query]);
  const keywordSuggestions = useMemo(
    () =>
      results.slice(0, 5).map((art) => ({
        id: `kb-${art.id}`,
        kind: "help",
        title: art.title,
        subtitle: art.summary || art.categoryTitle || "",
        href: `/admin/help#${art.id}`,
        ai: false,
      })),
    [results],
  );

  useEffect(() => {
    const q = query.trim();
    if (!aiEnabled || q.length < 2) {
      setAiHits([]);
      setAiLoading(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setAiLoading(true);
    (async () => {
      try {
        const result = await fetchAiSearch(q, { scopes: ["help"], limit: 5, signal: controller.signal });
        if (cancelled) return;
        setAiHits((result.items || []).map(normalizeSearchRow));
      } catch {
        if (!cancelled) setAiHits([]);
      } finally {
        if (!cancelled) setAiLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [aiEnabled, query]);

  const suggestionHits = aiHits.length > 0 ? aiHits : keywordSuggestions;
  const suggestionsAreAi = aiHits.length > 0;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const art of results) {
      const key = art.categoryId;
      if (!map.has(key)) {
        map.set(key, { title: art.categoryTitle, articles: [] });
      }
      map.get(key).articles.push(art);
    }
    return HELP_CATEGORIES.filter((c) => map.has(c.id)).map((c) => ({
      ...c,
      articles: map.get(c.id).articles,
    }));
  }, [results]);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="relative max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" aria-hidden />
        <input
          type="search"
          className="input-dark input-dark--with-icon w-full"
          placeholder="Search knowledge base…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="help-kb-search"
        />
      </div>
      <p className="text-xs text-[var(--text-tertiary)]">
        {results.length} article{results.length === 1 ? "" : "s"}
        {query.trim() ? ` matching “${query.trim()}”` : " across all EMS modules"}
      </p>

      {aiEnabled && query.trim().length >= 2 ? (
        <div className="surface p-4 border border-[var(--primary)]/25" data-testid="help-ai-results">
          <div className="flex items-center gap-2 text-[var(--primary)] mb-2">
            <Sparkles size={14} />
            <span className="text-[10px] font-mono uppercase tracking-widest">
              {suggestionsAreAi ? "AI suggestions" : "Top matches"}
            </span>
          </div>
          {aiLoading && !suggestionHits.length ? (
            <p className="text-xs text-[var(--text-tertiary)]">Searching help articles…</p>
          ) : suggestionHits.length > 0 ? (
            <ul className="space-y-2">
              {suggestionHits.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="text-left w-full"
                    onClick={() => {
                      const aid = String(row.href || "").includes("#")
                        ? String(row.href).split("#").pop()
                        : String(row.id || "").replace(/^kb-/, "").replace(/^help-/, "");
                      if (aid) setOpenId(aid);
                    }}
                  >
                    <span className="text-sm text-[var(--text-primary)] hover:text-[var(--primary)]">{row.title}</span>
                    {row.subtitle ? (
                      <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-0.5">{row.subtitle}</p>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-[var(--text-tertiary)]">No matches — try different keywords or open <Link to="/admin/ai" className="text-[var(--primary)] underline">AI Search</Link>.</p>
          )}
        </div>
      ) : null}

      {grouped.length === 0 ? (
        <div className="surface p-8 text-sm text-[var(--text-secondary)]">No articles match your search.</div>
      ) : (
        grouped.map((cat) => (
          <section key={cat.id} className="space-y-2">
            <h3 className="text-overline">{cat.title}</h3>
            {cat.description ? (
              <p className="text-xs text-[var(--text-tertiary)] -mt-1 mb-2">{cat.description}</p>
            ) : null}
            <div className="space-y-2">
              {cat.articles.map((art) => {
                const expanded = openId === art.id;
                return (
                  <div
                    key={art.id}
                    className="surface border border-[var(--border-default)]"
                    data-testid={`help-article-${art.id}`}
                  >
                    <button
                      type="button"
                      className="w-full flex items-start gap-3 p-4 text-left hover:bg-[var(--bg-elev)] transition-colors"
                      onClick={() => setOpenId(expanded ? null : art.id)}
                      aria-expanded={expanded}
                    >
                      {expanded ? (
                        <ChevronDown size={16} className="shrink-0 mt-0.5 text-[var(--primary)]" />
                      ) : (
                        <ChevronRight size={16} className="shrink-0 mt-0.5 text-[var(--text-tertiary)]" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">{art.title}</div>
                        <div className="text-xs text-[var(--text-secondary)] mt-1">{art.summary}</div>
                      </div>
                    </button>
                    {expanded ? (
                      <div className="px-4 pb-4 pt-0 ml-7 space-y-4 border-t border-[var(--border-default)]">
                        {(art.sections || []).map((sec) => (
                          <div key={sec.heading}>
                            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">
                              {sec.heading}
                            </div>
                            <ul className="text-sm text-[var(--text-secondary)] space-y-1.5 list-disc pl-4">
                              {(sec.body || []).map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        {(art.relatedLinks || []).length > 0 ? (
                          <div className="flex flex-wrap gap-2 pt-2">
                            {art.relatedLinks.map((link) => (
                              <Link
                                key={link.to + link.label}
                                to={link.to}
                                className="text-xs font-mono text-[var(--primary)] hover:underline inline-flex items-center gap-1"
                              >
                                {link.label} <ChevronRight size={12} />
                              </Link>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function SupportPanel() {
  const session = getAuthSession();
  const [form, setForm] = useState({
    subject: "",
    module: "general",
    priority: "normal",
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error("Describe the issue", { description: "Include steps to reproduce or the error you see." });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/help/request`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "content-type": "application/json" },
        body: JSON.stringify({
          type: "support",
          subject: form.subject.trim() || "Support request",
          module: form.module,
          priority: form.priority,
          message: form.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Request failed (${res.status})`);
      toast.success("Support request sent", {
        description: data?.emailSent
          ? "Our team will reply to your workspace email."
          : "Logged — email delivery may be delayed.",
      });
      setForm({ subject: "", module: "general", priority: "normal", message: "" });
    } catch (err) {
      toast.error("Could not send request", { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
      <div className="space-y-4">
        <div className="surface p-6 space-y-3">
          <div className="flex items-center gap-2 text-overline">
            <LifeBuoy size={12} /> Contact options
          </div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            For production incidents, submit the form — we attach your workspace and signed-in email (
            <span className="font-mono text-[var(--text-primary)]">{session?.email || "—"}</span>
            ) automatically.
          </p>
          <ul className="text-sm space-y-2 text-[var(--text-secondary)]">
            <li>
              Email:{" "}
              <a href="mailto:support@aivoryems.com" className="text-[var(--primary)] hover:underline font-mono">
                support@aivoryems.com
              </a>
            </li>
            <li>
              Status:{" "}
              <Link to="/status" className="text-[var(--primary)] hover:underline inline-flex items-center gap-1">
                aivoryems.com/status <ExternalLink size={12} />
              </Link>
            </li>
            <li>
              Security / account recovery:{" "}
              <Link to="/security" className="text-[var(--primary)] hover:underline">
                Security page
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <form onSubmit={submit} className="surface p-6 space-y-4" data-testid="help-support-form">
        <div className="text-overline">Submit a ticket</div>
        <div>
          <label className="text-overline">Subject</label>
          <input
            className="input-dark mt-2 w-full"
            value={form.subject}
            onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
            placeholder="Brief summary"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-overline">Module</label>
            <select
              className="input-dark mt-2 w-full"
              value={form.module}
              onChange={(e) => setForm((p) => ({ ...p, module: e.target.value }))}
            >
              {HELP_MODULE_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-overline">Priority</label>
            <select
              className="input-dark mt-2 w-full"
              value={form.priority}
              onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High — blocking work</option>
              <option value="urgent">Urgent — live event</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-overline">Details</label>
          <textarea
            className="input-dark mt-2 w-full min-h-[140px]"
            value={form.message}
            onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
            placeholder="Steps to reproduce, event name, screenshots, error messages…"
            required
          />
        </div>
        <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
          {busy ? "Sending…" : (
            <>
              <Send size={14} /> Send to support
            </>
          )}
        </button>
      </form>
    </div>
  );
}

function FeatureRequestPanel() {
  const [form, setForm] = useState({
    title: "",
    module: "general",
    impact: "nice_to_have",
    description: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description required");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/help/request`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "content-type": "application/json" },
        body: JSON.stringify({
          type: "feature_request",
          subject: form.title.trim(),
          module: form.module,
          priority: form.impact,
          message: form.description.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Request failed (${res.status})`);
      toast.success("Feature request received", {
        description: "Thank you — product reviews incoming requests regularly.",
      });
      setForm({ title: "", module: "general", impact: "nice_to_have", description: "" });
    } catch (err) {
      toast.error("Could not submit", { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
      <div className="surface p-6 space-y-3">
        <div className="flex items-center gap-2 text-overline">
          <Lightbulb size={12} /> How we use feedback
        </div>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          Feature requests help prioritize the roadmap. Include the workflow you are trying to solve and who benefits
          (operators, attendees, finance). Duplicate ideas are merged — you do not need to vote twice.
        </p>
        <p className="text-xs text-[var(--text-tertiary)]">
          For bugs or something broken today, use the Support tab instead.
        </p>
      </div>

      <form onSubmit={submit} className="surface p-6 space-y-4" data-testid="help-feature-form">
        <div className="text-overline">Suggest a feature</div>
        <div>
          <label className="text-overline">Title</label>
          <input
            className="input-dark mt-2 w-full"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Short name for the idea"
            required
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-overline">Module</label>
            <select
              className="input-dark mt-2 w-full"
              value={form.module}
              onChange={(e) => setForm((p) => ({ ...p, module: e.target.value }))}
            >
              {HELP_MODULE_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-overline">Impact</label>
            <select
              className="input-dark mt-2 w-full"
              value={form.impact}
              onChange={(e) => setForm((p) => ({ ...p, impact: e.target.value }))}
            >
              <option value="nice_to_have">Nice to have</option>
              <option value="workflow">Improves daily workflow</option>
              <option value="revenue">Revenue / conversion</option>
              <option value="compliance">Compliance / security</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-overline">Description</label>
          <textarea
            className="input-dark mt-2 w-full min-h-[160px]"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="What problem does this solve? How do you work around it today?"
            required
          />
        </div>
        <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
          {busy ? "Submitting…" : (
            <>
              <Send size={14} /> Submit feature request
            </>
          )}
        </button>
      </form>
    </div>
  );
}

function AiChatbotPanel({ aiEnabled = false }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState([]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = message.trim();
    if (!text || busy) return;
    setMessage("");
    setTurns((prev) => [...prev, { role: "user", text }]);
    setBusy(true);
    try {
      const body = await askAiChatbot({ message: text });
      setTurns((prev) => [...prev, { role: "assistant", text: body.reply || body.message || "No response.", citations: body.citations || [] }]);
    } catch (err) {
      toast.error(err?.message || "Chat failed");
      setTurns((prev) => [...prev, { role: "assistant", text: "Sorry — I could not reach the AI assistant. Try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  if (!aiEnabled) {
    return (
      <div className="surface p-6 max-w-2xl text-sm text-[var(--text-secondary)]">
        AI chatbot is available on Scale plans and above. Upgrade in Settings → Billing or contact support.
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4" data-testid="help-ai-chatbot">
      <p className="text-sm text-[var(--text-secondary)]">
        Ask how to use AivoryEMS — registration, comms, check-in, page builder, and more. Answers cite help articles when available.
      </p>
      <div className="surface border border-[var(--border-default)] rounded-xl min-h-[280px] max-h-[420px] overflow-y-auto p-4 space-y-4">
        {turns.length === 0 ? (
          <p className="text-xs text-[var(--text-tertiary)]">Try: “How do I publish a registration form?” or “Export attendee list to CSV”</p>
        ) : null}
        {turns.map((t, i) => (
          <div key={i} className={t.role === "user" ? "text-right" : ""}>
            <div className={`inline-block max-w-[90%] text-sm rounded-lg px-3 py-2 ${t.role === "user" ? "bg-[var(--primary)] text-[var(--on-primary,#fff)]" : "bg-[var(--bg-elev)] text-[var(--text-primary)]"}`}>
              {t.text}
            </div>
            {t.citations?.length ? (
              <ul className="mt-2 text-[10px] text-[var(--text-tertiary)] space-y-1">
                {t.citations.slice(0, 3).map((c, j) => (
                  <li key={j}>↳ {c.title || c.snippet || c.id}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
        {busy ? <p className="text-xs text-[var(--text-tertiary)]">Thinking…</p> : null}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input
          className="input-dark flex-1"
          placeholder="Ask AivoryONE (Beta)…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={busy}
        />
        <button type="submit" className="btn-primary shrink-0" disabled={busy || !message.trim()}>
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}

export default function GetHelp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [aiEnabled, setAiEnabled] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const tab = HELP_TABS.some((t) => t.id === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "kb";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/capabilities`, {
          cache: "no-store",
          headers: getAuthHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const flags = data?.featureFlags || {};
        const features = data?.features || [];
        setAiEnabled(flags.ai_search === true || features.includes("ai_search"));
        setChatbotEnabled(flags.ai_chatbot === true || features.includes("ai_chatbot"));
      } catch {
        if (!cancelled) {
          setAiEnabled(false);
          setChatbotEnabled(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setTab = (id) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] mx-auto">
      <div className="flex items-start gap-3 mb-2">
        <BookOpen size={22} className="text-[var(--primary)] shrink-0 mt-1" />
        <div>
          <div className="text-overline">Help center</div>
          <h1 className="font-heading text-3xl lg:text-4xl tracking-tight font-medium mt-1">Get help</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl leading-relaxed">
            Documentation for every EMS module, plus support and product feedback — without leaving the admin console.
          </p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--border-default)] mt-8 mb-8">
        {HELP_TABS.map((t) => (
          <HelpTabButton key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </HelpTabButton>
        ))}
      </div>

      {tab === "kb" ? <KnowledgeBasePanel aiEnabled={aiEnabled} /> : null}
      {tab === "chat" ? <AiChatbotPanel aiEnabled={chatbotEnabled} /> : null}
      {tab === "support" ? <SupportPanel /> : null}
      {tab === "feature" ? <FeatureRequestPanel /> : null}
    </div>
  );
}
